import React, { useEffect, useRef, useState } from "react";
import SucessMsgQb from "./SucessMsgQb";
import axios from "./../../axios";
import ErrorDialog from "./ErrorDialogue";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { AlertTriangle } from "lucide-react";

const ACCENT = "#f77f00";
const ACCENT_SOFT = "#fff4e5";
const BORDER = "#e6e9ef";
const TEXT_PRIMARY = "#1f2733";
const TEXT_SECONDARY = "#5b6472";

const ConfirmPopUp = ({ close, questions, basicDetails, type }) => {
  const [success, setSuccess] = useState(false);
  const qbDialogRef = useRef(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [isGettingData, setIsGettingData] = useState(false);

  let Teacher_id = localStorage.getItem("Teacher_id");
  let Teacher_Json = JSON.parse(Teacher_id);

  const dateHelper = (a) => {
    const inputDate = new Date(a);
    const month = inputDate.getMonth() + 1;
    const day = inputDate.getDate();
    const year = inputDate.getFullYear();
    const formattedDate = `${year}-${month}-${day}`;
    return formattedDate;
  };

  const handleUpdateQB = async () => {
    setIsGettingData(true);
    if (type === "create") {
      questions = questions.map((val) => {
        if (val.is_question_edited || val.id !== undefined) {
          delete val.id;
        }
        return val;
      });
      let body = {
        name: basicDetails.name,
        standard_id: basicDetails.standard_id.id,
        subject_id: basicDetails.subject_id.id,
        teacher_id: Teacher_Json,
        section_id: basicDetails.section_id.id,
        questions: questions,
      };
      try {
        let res = await axios.post(`/api/v1/questionBank/create`, body);
        if (res.status === 201) {
          setSuccess(true);
          setIsGettingData(true);
          localStorage.removeItem("QBIMP");
        }
      } catch (error) {
        console.log("Error : ", error);
        setIsError(true);
        if (error.response.data.message) {
          setErrorMessage(error.response.data.message);
        }
        if (qbDialogRef.current) {
          qbDialogRef.current.openDialog();
        }
      } finally {
        setIsGettingData(false);
        localStorage.removeItem("temp_Questions_QUESTION_BANK");
      }
    } else {
      questions = questions.map((val) => {
        if (val.is_question_edited && val.id !== undefined) {
          delete val.id;
        }
        return val;
      });
      let body = {
        name: basicDetails.name,
        standard_id: basicDetails.standard_id.id,
        subject_id: basicDetails.subject_id.id,
        teacher_id: Teacher_Json,
        section_id: basicDetails.section_id.id,
        deadline: dateHelper(basicDetails.deadline),
        questions: questions,
      };
      try {
        let res = await axios.put(
          `/api/v1/questionBank/update/${basicDetails.id}`,
          body,
        );
        if (res.status === 200) {
          setSuccess(true);
          setIsGettingData(true);
          localStorage.removeItem("QBIMP");
        }
      } catch (error) {
        setIsError(true);
        if (error.response.data.message) {
          setErrorMessage(error.response.data.message);
        }
      } finally {
        setIsGettingData(false);
        localStorage.removeItem("temp_Questions_QUESTION_BANK");
      }
    }
  };

  useEffect(() => {
    if (isError) {
      if (qbDialogRef.current) {
        qbDialogRef.current.openDialog();
      }
    }
  }, [isError]);

  // Success screen
  if (success) {
    return <SucessMsgQb details={basicDetails} />;
  }

  return (
    <>
      <Dialog
        open={!success}
        onClose={() => close(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            p: 1,
          },
        }}
      >
        {/* Loading Overlay */}
        {isGettingData && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              background: "rgba(255,255,255,0.85)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              borderRadius: "16px",
            }}
          >
            <CircularProgress size={40} sx={{ color: ACCENT }} />
            <Typography
              sx={{ fontSize: "13px", fontWeight: 600, color: TEXT_SECONDARY }}
            >
              Saving to Question Bank...
            </Typography>
          </Box>
        )}

        <DialogContent sx={{ p: 3, position: "relative" }}>
          {/* Close Button */}
          <IconButton
            onClick={() => close(false)}
            size="small"
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              color: TEXT_SECONDARY,
              background: "#f8f9fb",
              border: `1px solid ${BORDER}`,
              "&:hover": { background: ACCENT_SOFT, color: ACCENT },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          {/* Icon + Header */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              mb: 2,
              mt: 1,
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                background: ACCENT_SOFT,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1.5,
              }}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 32, color: ACCENT }} />
            </Box>

            <Typography
              sx={{
                fontSize: "17px",
                fontWeight: 700,
                color: TEXT_PRIMARY,
                mb: 0.5,
              }}
            >
              Save to Question Bank
            </Typography>

            <Typography
              sx={{
                fontSize: "13px",
                color: TEXT_SECONDARY,
                lineHeight: 1.5,
                maxWidth: "300px",
              }}
            >
              Are you sure you want to save these{" "}
              <b>{questions?.length || 0} questions</b> in the Question Bank?
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Buttons */}
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => close(false)}
              disabled={isGettingData}
              sx={{
                borderColor: BORDER,
                color: TEXT_SECONDARY,
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13.5px",
                py: 1,
                "&:hover": {
                  borderColor: TEXT_SECONDARY,
                  background: "#f8f9fb",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleUpdateQB}
              disabled={isGettingData}
              sx={{
                background: ACCENT,
                color: "#fff",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "13.5px",
                py: 1,
                boxShadow: "0 4px 12px rgba(247,127,0,0.25)",
                "&:hover": {
                  background: "#e56f00",
                  boxShadow: "0 6px 16px rgba(247,127,0,0.35)",
                },
              }}
            >
              Yes, Save
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Error Dialog */}
      {isError && (
        <ErrorDialog
          error={"Cannot evaluate Question Bank!"}
          message={errorMessage}
          ref={qbDialogRef}
          setIsError={setIsError}
        />
      )}
    </>
  );
};

export default ConfirmPopUp;
